import { Check, Heart, Library, MoreHorizontal, Truck } from 'lucide-react';
import type { KeyboardEvent, MouseEvent } from 'react';
import { Link } from 'react-router-dom';

import type { TrackCard } from '../../api/index.js';
import { Button } from '../../design-system/button.js';
import { StatusPill } from '../../design-system/status-pill.js';
import { cn } from '../../design-system/cn.js';
import {
  PlaybackButton,
  usePlaybackControl,
  type PlaybackControl,
  type PlaybackItem,
} from '../../player/index.js';

export function trackTitle(track: TrackCard) {
  return track.display_metadata.title ?? track.provider_title ?? 'Untitled track';
}

export function trackArtist(track: TrackCard) {
  return track.display_metadata.artist ?? 'Unknown artist';
}

export function formatDuration(seconds: number | null) {
  if (seconds === null || !Number.isFinite(seconds)) return '—';
  const whole = Math.max(0, Math.round(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

export function TrackSeams({ track, compact = false }: { track: TrackCard; compact?: boolean }) {
  const deliveryLabel =
    track.delivery_summary.total > 0
      ? track.delivery_summary.any_succeeded
        ? 'Delivered'
        : `${track.delivery_summary.total} delivery ${track.delivery_summary.total === 1 ? 'request' : 'requests'}`
      : null;

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', compact && 'gap-1')}>
      <StatusPill status={track.status}>{track.status}</StatusPill>
      {track.favorite === true ? (
        <StatusPill status="liked" tone="accent">
          <Heart aria-hidden="true" className="size-3 fill-current" /> Liked
        </StatusPill>
      ) : null}
      {track.library_import ? (
        <>
          <StatusPill status={track.library_import.status}>
            <Library aria-hidden="true" className="size-3" />{' '}
            {track.library_import.status === 'available'
              ? 'In Library'
              : track.library_import.status}
          </StatusPill>
          {track.library_import.phase &&
          track.library_import.phase !== track.library_import.status ? (
            <StatusPill status={track.library_import.phase}>
              {track.library_import.phase}
            </StatusPill>
          ) : null}
        </>
      ) : null}
      {deliveryLabel ? (
        <StatusPill status={track.delivery_summary.any_succeeded ? 'succeeded' : 'pending'}>
          <Truck aria-hidden="true" className="size-3" /> {deliveryLabel}
        </StatusPill>
      ) : null}
    </div>
  );
}

export type TrackPlaybackAction = PlaybackControl;

export function playbackItemFromTrack(track: TrackCard): PlaybackItem | null {
  const owned = track.library_import?.status === 'available';
  const title = trackTitle(track);
  const playable = owned || track.preview.available;
  const common = {
    id: track.id,
    title,
    artist: track.display_metadata.artist,
    album: track.display_metadata.album,
    detailHref: `/tracks/${track.id}`,
    ...(track.generation ? { composerProjectId: track.generation.composer_project.id } : {}),
  };
  return !playable
    ? null
    : owned && track.library_import
      ? {
          ...common,
          sourcePreference: 'owned',
          libraryId: track.library_import.library_id,
          importId: track.library_import.id,
        }
      : { ...common, sourcePreference: 'preview' };
}

export function useTrackPlaybackAction(
  track: TrackCard,
  start?: () => Promise<void> | void,
): TrackPlaybackAction | null {
  return usePlaybackControl(playbackItemFromTrack(track), start);
}

function Artwork({ title }: { title: string }) {
  return (
    <div
      aria-hidden="true"
      className="grid size-12 shrink-0 place-items-center rounded-md border border-border bg-[linear-gradient(135deg,var(--surface-hi),var(--bg-2))] font-mono text-xs font-semibold text-ink-3"
    >
      {title.trim().charAt(0).toUpperCase() || '•'}
    </div>
  );
}

function ArtworkPlaybackButton({
  action,
  title,
}: {
  action: TrackPlaybackAction | null;
  title: string;
}) {
  return (
    <PlaybackButton
      control={action}
      className="size-12 rounded-md border-border bg-[linear-gradient(135deg,var(--surface-hi),var(--bg-2))] text-ink-2 hover:border-accent-line hover:text-ink focus-visible:outline-accent [&_svg]:size-5"
      inactiveContent={<span aria-hidden="true" />}
      size="icon"
      unavailableLabel={`Playback unavailable for ${title}`}
      variant="ghost"
    />
  );
}

export interface TrackSelectionAction {
  readonly selected: boolean;
  readonly onToggle: () => void;
}

function SelectionIndicator({ selected }: { selected: boolean }) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        'grid size-11 shrink-0 place-items-center rounded-full border transition-colors',
        selected
          ? 'border-accent bg-accent text-bg'
          : 'border-border-hi bg-bg-2 text-transparent hover:border-accent',
      )}
    >
      <Check aria-hidden="true" className="size-5" strokeWidth={2.5} />
    </div>
  );
}

function selectionSurfaceProps(selection: TrackSelectionAction | undefined) {
  if (!selection) return {};
  return {
    'aria-checked': selection.selected,
    onClick: (event: MouseEvent<HTMLElement>) => {
      if ((event.target as HTMLElement).closest('button')) return;
      selection.onToggle();
    },
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
      if (event.key !== ' ' && event.key !== 'Enter') return;
      event.preventDefault();
      selection.onToggle();
    },
    role: 'checkbox',
    tabIndex: 0,
  } as const;
}

export function TrackRow({
  action,
  showProject = true,
  showSeams = true,
  selection,
  track,
  trailing,
}: {
  action: TrackPlaybackAction | null;
  showProject?: boolean;
  showSeams?: boolean;
  selection?: TrackSelectionAction;
  track: TrackCard;
  trailing?: React.ReactNode;
}) {
  const title = trackTitle(track);
  return (
    <article
      {...selectionSurfaceProps(selection)}
      className={cn(
        'hidden min-h-[4.75rem] items-center gap-4 border-b border-border px-4 py-3 last:border-b-0 md:grid',
        selection && 'cursor-pointer outline-none focus-visible:bg-surface-hi',
        selection?.selected && 'bg-accent/8',
        showSeams
          ? 'grid-cols-[3rem_minmax(12rem,1fr)_minmax(14rem,0.8fr)_auto]'
          : 'grid-cols-[3rem_minmax(12rem,1fr)_auto]',
      )}
    >
      {selection ? (
        <SelectionIndicator selected={selection.selected} />
      ) : (
        <ArtworkPlaybackButton action={action} title={title} />
      )}
      <div className="min-w-0">
        {selection ? (
          <span className="block truncate text-sm font-semibold text-ink">{title}</span>
        ) : (
          <Link
            className="block truncate text-sm font-semibold text-ink outline-none hover:text-accent focus-visible:text-accent"
            to={`/tracks/${track.id}`}
          >
            {title}
          </Link>
        )}
        <div className="mt-1 flex min-w-0 items-center gap-2 font-mono text-xs text-ink-3">
          <span className="truncate">{trackArtist(track)}</span>
          <span aria-hidden="true">·</span>
          <span className="shrink-0">{formatDuration(track.duration_seconds)}</span>
          {showProject && track.provider_project?.title ? (
            <>
              <span aria-hidden="true">·</span>
              <span className="truncate">{track.provider_project.title}</span>
            </>
          ) : null}
        </div>
      </div>
      {showSeams ? <TrackSeams track={track} compact /> : null}
      <div className="flex min-w-11 justify-end">
        {selection
          ? null
          : (trailing ?? (
              <Button asChild aria-label={`Open ${title}`} size="icon" variant="ghost">
                <Link to={`/tracks/${track.id}`}>
                  <MoreHorizontal aria-hidden="true" />
                </Link>
              </Button>
            ))}
      </div>
    </article>
  );
}

export function TrackCardSurface({
  action,
  showSeams = true,
  selection,
  track,
  trailing,
}: {
  action: TrackPlaybackAction | null;
  showSeams?: boolean;
  selection?: TrackSelectionAction;
  track: TrackCard;
  trailing?: React.ReactNode;
}) {
  const title = trackTitle(track);
  return (
    <article
      {...selectionSurfaceProps(selection)}
      className={cn(
        'rounded-md border border-border bg-surface md:hidden',
        showSeams ? 'p-4' : 'p-3',
        selection && 'cursor-pointer outline-none focus-visible:border-accent',
        selection?.selected && 'border-accent bg-accent/8',
      )}
    >
      <div className={cn('flex gap-3', showSeams ? 'items-start' : 'items-center')}>
        {selection ? (
          <Artwork title={title} />
        ) : (
          <ArtworkPlaybackButton action={action} title={title} />
        )}
        <div className="min-w-0 flex-1">
          {selection ? (
            showSeams ? (
              <span className="-my-3 block min-h-11 truncate py-3 text-sm font-semibold text-ink">
                {title}
              </span>
            ) : (
              <span className="grid min-h-11 min-w-0 content-center">
                <span className="truncate text-sm font-semibold text-ink">{title}</span>
                <span className="mt-1 flex min-w-0 items-center gap-1.5 font-mono text-xs text-ink-3">
                  <span className="truncate">{trackArtist(track)}</span>
                  <span aria-hidden="true">·</span>
                  <span className="shrink-0">{formatDuration(track.duration_seconds)}</span>
                </span>
              </span>
            )
          ) : (
            <Link
              className={cn(
                'min-w-0 outline-none hover:text-accent focus-visible:text-accent',
                showSeams
                  ? '-my-3 block min-h-11 truncate py-3 text-sm font-semibold text-ink'
                  : 'grid min-h-11 content-center',
              )}
              to={`/tracks/${track.id}`}
            >
              {showSeams ? (
                title
              ) : (
                <>
                  <span className="truncate text-sm font-semibold text-ink">{title}</span>
                  <span className="mt-1 flex min-w-0 items-center gap-1.5 font-mono text-xs text-ink-3">
                    <span className="truncate">{trackArtist(track)}</span>
                    <span aria-hidden="true">·</span>
                    <span className="shrink-0">{formatDuration(track.duration_seconds)}</span>
                  </span>
                </>
              )}
            </Link>
          )}
          {showSeams ? (
            <p className="mt-1 truncate font-mono text-xs text-ink-3">
              {trackArtist(track)} · {formatDuration(track.duration_seconds)}
            </p>
          ) : null}
        </div>
        {selection || (!showSeams && trailing) ? (
          <div className={cn('flex shrink-0 items-center', showSeams ? 'gap-2' : 'gap-1')}>
            {selection ? <SelectionIndicator selected={selection.selected} /> : trailing}
          </div>
        ) : null}
      </div>
      {showSeams ? (
        <div className="mt-3 flex items-end justify-between gap-3 border-t border-border pt-3">
          <TrackSeams track={track} compact />
          {!selection && trailing ? <div className="shrink-0">{trailing}</div> : null}
        </div>
      ) : null}
    </article>
  );
}
